import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { authApi } from "../../api/axios";

/**
 * Get all user roles
 */
export const getUserRoles = createAsyncThunk(
  "user/getUserRoles",
  async ({ storeId }, { rejectWithValue }) => {
    try {
      const res = await authApi.get(`/v1/stores/users/roles?store_id=${storeId}`);
      return res.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

/**
 * Get all users by store
 */
export const getUsersByStore = createAsyncThunk(
  "user/getUsersByStore",
  async ({ storeId }, { rejectWithValue }) => {
    try {
      const res = await authApi.get(`/v1/stores/users?store_id=${storeId}`);
      const responseData = res.data?.data ?? res.data ?? {};
      return {
        users: responseData.users ?? [],
        pagination: responseData.pagination ?? { limit: 0, offset: 0, total: 0 },
      };
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

/**
 * Delete user
 */
export const userDelete = createAsyncThunk(
  "user/userDelete",
  async (obj, { rejectWithValue }) => {
    try {
      const res = await authApi.patch("/v1/stores/users/action", obj);
      const dataObj = {
        ...res.data,
        ...obj,
      };
      return dataObj; // return deleted payload to update state
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

/**
 * Perform user action (e.g. activate/deactivate)
 */
export const userAction = createAsyncThunk(
  "user/userAction",
  async (obj, { rejectWithValue }) => {
    try {
      const res = await authApi.patch("/v1/stores/users/action", obj);
      const dataObj = {
        ...res.data,
        ...obj,
      };
      return dataObj; // return updated user action info
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

/**
 * Add user
 */
export const addUser = createAsyncThunk(
  "user/addUser",
  async (obj, { rejectWithValue }) => {
    try {
      const res = await authApi.post("/v1/stores/users", obj);
      return res.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

/**
 * Update user
 */
export const updateUser = createAsyncThunk(
  "user/updateUser",
  async (obj, { rejectWithValue }) => {
    try {
      const res = await authApi.put("/v1/stores/users", obj);
      return res.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);
/**Get user by uid */
export const getUserByUid = createAsyncThunk(
  "user/getUserByUid",
  async ({ storeId, uid }, { rejectWithValue }) => {
    try {
      const res = await authApi.get(
        `/v1/stores/users/details?store_id=${storeId}&user_id=${uid}`,
      );
      return res.data;
    } catch (err) {
      return rejectWithValue(err);
    }
  },
);

// Slice
const userSlice = createSlice({
  name: "user",
  initialState: {
    list: [],
    pagination: { limit: 0, offset: 0, total: 0 },
    roles: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearUserState: (state) => {
      state.list = [];
      state.pagination = { limit: 0, offset: 0, total: 0 };
      state.roles = [];
      state.loading = false;
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // GET
      .addCase(getUserRoles.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUserRoles.fulfilled, (state, action) => {
        state.loading = false;
        state.roles = action.payload;
      })
      .addCase(getUserRoles.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Get Users by Store
      .addCase(getUsersByStore.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getUsersByStore.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload.users;
        state.pagination = action.payload.pagination;
      })
      .addCase(getUsersByStore.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // DELETE
      .addCase(userDelete.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(userDelete.fulfilled, (state, action) => {
        state.loading = false;
        const deleted = action.payload;
        state.list = state.list.filter((c) => c.id !== deleted.id);
      })
      .addCase(userDelete.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // ACTION
      .addCase(userAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(userAction.fulfilled, (state, action) => {
        state.loading = false;
        const { uid, record_status } = action.payload;
        state.list = state.list.map((a) =>
          a.uid === uid ? { ...a, record_status } : a,
        );
      })
      .addCase(userAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // ADD
      .addCase(addUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addUser.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(addUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // UPDATE
      .addCase(updateUser.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUser.fulfilled, (state, action) => {
        state.loading = false;
      })
      .addCase(updateUser.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearUserState } = userSlice.actions;
export default userSlice.reducer;
